/**
 * SyncManager — Orchestrates data synchronization between server and local DryStore.
 *
 * Pre-download strategy:
 * 1. On login: Full pull of form templates, assets, sites, asset types into IndexedDB
 * 2. Background: Incremental pull every 30s using updated_since checkpoint
 * 3. On submission: Push to server immediately if online, queue if offline
 *
 * Checkpoint-based sync:
 * - Each entity type tracks its last sync timestamp in localStorage
 * - Incremental pulls only fetch records updated after the checkpoint
 * - Full pull on first login or when no checkpoint exists
 */

import type { Collection } from '@drystuff/store';
import type { HttpClient } from '@drystuff/connect';
import { config } from './config';

export interface SyncCollections {
  users: Collection<any>;
  assets: Collection<any>;
  assetTypes: Collection<any>;
  sites: Collection<any>;
  formTemplates: Collection<any>;
  formSubmissions: Collection<any>;
}

export interface SyncManagerOptions {
  http: HttpClient;
  collections: SyncCollections;
  onSyncStart?: () => void;
  onSyncComplete?: (stats: SyncStats) => void;
  onSyncError?: (error: Error) => void;
}

export interface SyncStats {
  assets: number;
  assetTypes: number;
  sites: number;
  formTemplates: number;
  duration: number;
  isIncremental: boolean;
}

const CHECKPOINT_PREFIX = 'drysync_checkpoint_';

export class SyncManager {
  private http: HttpClient;
  private collections: SyncCollections;
  private pullTimer: ReturnType<typeof setInterval> | null = null;
  private isSyncing = false;
  private options: SyncManagerOptions;

  constructor(options: SyncManagerOptions) {
    this.http = options.http;
    this.collections = options.collections;
    this.options = options;
  }

  // ──────────────────────────────────────────────────
  // Checkpoint management
  // ──────────────────────────────────────────────────

  private getCheckpoint(entity: string): string | null {
    return localStorage.getItem(CHECKPOINT_PREFIX + entity);
  }

  private setCheckpoint(entity: string) {
    localStorage.setItem(CHECKPOINT_PREFIX + entity, new Date().toISOString());
  }

  private clearCheckpoints() {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith(CHECKPOINT_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  }

  // ──────────────────────────────────────────────────
  // Public API
  // ──────────────────────────────────────────────────

  /**
   * Initial sync — called after login.
   * Downloads all data the user needs for offline operation.
   */
  async initialSync(): Promise<SyncStats> {
    if (this.isSyncing) throw new Error('Sync already in progress');
    this.isSyncing = true;
    this.options.onSyncStart?.();
    const start = performance.now();

    const stats: SyncStats = {
      assets: 0, assetTypes: 0, sites: 0, formTemplates: 0,
      duration: 0, isIncremental: false,
    };

    try {
      const [atResult, sitesResult, formsResult, assetsResult] = await Promise.allSettled([
        this.pullEntity('assetTypes', '/asset-types', this.collections.assetTypes, { limit: 500 }),
        this.pullEntity('sites', '/sites', this.collections.sites, { limit: 500 }),
        this.pullEntity('formTemplates', '/forms', this.collections.formTemplates, { status: 'published', limit: 500 }),
        this.pullEntityPaginated('assets', '/assets', this.collections.assets),
      ]);

      if (atResult.status === 'fulfilled') stats.assetTypes = atResult.value;
      if (sitesResult.status === 'fulfilled') stats.sites = sitesResult.value;
      if (formsResult.status === 'fulfilled') stats.formTemplates = formsResult.value;
      if (assetsResult.status === 'fulfilled') stats.assets = assetsResult.value;

      stats.duration = performance.now() - start;
      this.options.onSyncComplete?.(stats);
      console.log(`[SyncManager] Initial sync: ${stats.assets} assets, ${stats.formTemplates} forms, ${stats.sites} sites in ${Math.round(stats.duration)}ms`);
      return stats;
    } catch (err) {
      this.options.onSyncError?.(err as Error);
      throw err;
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Incremental sync — only fetches records updated since last checkpoint.
   */
  async incrementalSync(): Promise<SyncStats> {
    if (this.isSyncing) return { assets: 0, assetTypes: 0, sites: 0, formTemplates: 0, duration: 0, isIncremental: true };
    this.isSyncing = true;
    const start = performance.now();

    const stats: SyncStats = {
      assets: 0, assetTypes: 0, sites: 0, formTemplates: 0,
      duration: 0, isIncremental: true,
    };

    try {
      const [sitesResult, formsResult] = await Promise.allSettled([
        this.pullEntity('sites', '/sites', this.collections.sites, { limit: 500 }),
        this.pullEntity('formTemplates', '/forms', this.collections.formTemplates, { status: 'published', limit: 500 }),
      ]);

      if (sitesResult.status === 'fulfilled') stats.sites = sitesResult.value;
      if (formsResult.status === 'fulfilled') stats.formTemplates = formsResult.value;

      stats.duration = performance.now() - start;
      return stats;
    } finally {
      this.isSyncing = false;
    }
  }

  /**
   * Start periodic background sync.
   */
  startPeriodicSync() {
    if (this.pullTimer) return;
    this.pullTimer = setInterval(async () => {
      try { await this.incrementalSync(); } catch { /* silent */ }
    }, config.sync.pullInterval);
    console.log(`[SyncManager] Periodic sync started (every ${config.sync.pullInterval / 1000}s)`);
  }

  stopPeriodicSync() {
    if (this.pullTimer) {
      clearInterval(this.pullTimer);
      this.pullTimer = null;
    }
  }

  destroy() {
    this.stopPeriodicSync();
  }

  // ──────────────────────────────────────────────────
  // Private: entity pull with checkpoint
  // ──────────────────────────────────────────────────

  private async pullEntity(
    entityName: string,
    apiPath: string,
    collection: Collection<any>,
    baseParams?: Record<string, any>,
  ): Promise<number> {
    const checkpoint = this.getCheckpoint(entityName);
    const params: Record<string, any> = { ...baseParams };
    if (checkpoint) {
      params.updated_since = checkpoint;
    }

    const response = await this.http.get<any>(apiPath, { params });
    const data = response.data?.data || response.data || [];
    const items = Array.isArray(data) ? data : [];

    for (const item of items) {
      if (item?.id) await collection.upsert(item);
    }

    this.setCheckpoint(entityName);
    return items.length;
  }

  /**
   * Paginated pull for large collections (assets: 427+).
   * Uses updated_since for incremental, full pagination for initial.
   */
  private async pullEntityPaginated(
    entityName: string,
    apiPath: string,
    collection: Collection<any>,
  ): Promise<number> {
    const checkpoint = this.getCheckpoint(entityName);
    let page = 1;
    let total = 0;
    const limit = 100;

    while (true) {
      const params: Record<string, any> = { page, limit };
      if (checkpoint) params.updated_since = checkpoint;

      const response = await this.http.get<any>(apiPath, { params });
      const data = response.data?.data || [];
      const items = Array.isArray(data) ? data : [];

      if (items.length === 0) break;

      for (const item of items) {
        if (item?.id) await collection.upsert(item);
      }

      total += items.length;
      const serverTotal = response.data?.total || 0;
      if (total >= serverTotal || items.length < limit) break;
      page++;
    }

    this.setCheckpoint(entityName);
    return total;
  }
}
